//Input
import { useState, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';

import { IconButton as ChakraIconButton, FormControl } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { MdAccountTree } from 'react-icons/md';
import { Select } from 'chakra-react-select';
import { useQuery } from 'hooks/useCustomQuery';
import { useQuery as useTanstackQuery } from '@tanstack/react-query';

//internal
import {
    groupBy,
    relabel,
} from '../../components/presentational/Searchbox.jsx';

import { Image, FormLabel } from 'components/v4';

import { axios } from '@/services/axios';
import config from '../../config.json';
import { cannotNavigate } from '@/store/middlewares/modalChangesMiddleware';
import { useNavigateLocation } from '@/hooks/useNavigateLocation/useNavigateLocation';

import walkthroughIds from './walkthroughIds.js';
import { navbarSelectAccents } from './constant';
import { chakraSelectDarkMode, defaultChakraSelectStyle } from '@/constants';
import { floatingSelectStyles as _floatingSelectStyles } from '@/chakratheme.js';
import getCompany from 'API/Company/getCompany';
import getCompanyPlatforms from 'API/Company/getCompanyPlatforms';
import getCompanyNavigationList from 'API/Company/getCompanyNavigationList';

const useStyles = makeStyles((theme) =>
    createStyles({
        title: {
            display: 'none',
            [theme.breakpoints.up('sm')]: {
                display: 'block',
            },
        },
        search: {
            position: 'relative',
            borderRadius: theme.shape.borderRadius,
            width: '30%',
            paddingLeft: 0,
        },
        searchCompany: {
            position: 'relative',
            alignSelf: 'center',
            borderRadius: theme.shape.borderRadius,
            width: '40%',
            paddingLeft: 0,
        },
    }),
);

// Groups a flat companies/partners array (each item carries parentID/
// parentName, from relabel()) into chakra-react-select's native grouped
// option shape. Matches the ordering-independent grouping MUI's Autocomplete
// groupBy used to do, but without MUI's adjacent-sort requirement.
export const buildGroupedCompanyOptions = (companiesAndPartners) => {
    const groups = new Map();
    companiesAndPartners.forEach((option) => {
        const label = groupBy(option);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(option);
    });
    return Array.from(groups, ([label, options]) => ({ label, options }));
};

const Searchbar = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { handleNavigateLocation } = useNavigateLocation();

    const [changeWholesaler, setChangeWholesaler] = useState(false);
    const [shouldLoadWholesalers, setShouldLoadWholesalers] = useState(false);
    const [shouldLoadCompaniesAndPartners, setShouldLoadCompaniesAndPartners] =
        useState(false);

    const { colorScheme } = useSelector((state) => {
        return { ...state.settings };
    });
    const {
        companiesAndPartners,
        companiesAndPartnersLoading,
        wholesalers,
        currentCompany,
        currentWholesaler,
    } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { show, state, hash } = useSelector((state) => {
        return { ...state.modal };
    });
    const {
        defaultWholesaler,
        defaultCompany,
        defaultPartner,
        imagePathLightMode,
        imagePathDarkMode,
        requiredScope,
    } = useSelector((state) => {
        return { ...state.login };
    });
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname: currentPath } = location;

    const currentParentPath = location?.pathname?.split('/')[1];
    const isGA = requiredScope === config.scope.global;
    const isWAorAbove = requiredScope >= config.scope.wholesaler;

    const chakraSelectDark = chakraSelectDarkMode(true);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...navbarSelectAccents(colorScheme, chakraSelectDark),
        // Group heading (react-select's equivalent of MUI's ListSubheader,
        // used to label each parent/wholesaler's group of companies) —
        // matches the sidebar's own active-item background (colorMode._active
        // in components/sidebar/constants.js).
        groupHeading: (provided) => ({
            ...provided,
            background: 'whiteAlpha.300',
            color: 'white',
            borderRadius: '0.25rem',
        }),
    };

    const floatingSelectStyles = _floatingSelectStyles(true);
    const isCompaniesAndPartnersListLoading =
        shouldLoadCompaniesAndPartners && companiesAndPartnersLoading;
    // Matches the prior groupByProps={requiredScope > 20 && groupBy} —
    // Company-scope users see a flat list, everyone above sees it grouped
    // by parent/wholesaler.
    const companyOptions =
        requiredScope > config.scope.company
            ? buildGroupedCompanyOptions(companiesAndPartners)
            : companiesAndPartners;
    const shouldPrefetchCompanyPlatforms = Boolean(
        currentCompany && !currentPath.includes('/dashboard-services'),
    );
    const { data: currentCompanyQueryData } = useQuery({
        queryKey: ['company', currentCompany],
        queryFn: async () => {
            return getCompany(currentCompany);
        },
        enabled: !!currentCompany,
        refetchOnWindowFocus: false,
    });

    const currentCompanyIdentity = currentCompanyQueryData;
    const currentCompanyLabel =
        currentCompanyIdentity?.name ||
        currentCompanyIdentity?.entityName ||
        'Loading company...';
    const currentManagingValue =
        companiesAndPartners.find((item) => item.id === currentCompany) ||
        (currentCompany
            ? {
                  id: currentCompany,
                  name: currentCompanyLabel,
                  parentID: currentCompanyIdentity?.partnerID || '',
                  parentName: currentCompanyIdentity?.partnerName || '',
              }
            : null);

    useQuery({
        queryKey: [
            'services',
            `companyPlatforms: ${currentCompany}, requiredScope: ${requiredScope}`,
        ],
        queryFn: async () => {
            const res = await getCompanyPlatforms(
                requiredScope,
                currentCompany,
            );
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY_PLATFORMS',
                payload: res,
            });
            return res;
        },
        enabled: shouldPrefetchCompanyPlatforms,
        refetchOnWindowFocus: false,
    });

    // 2935: the wholesalers dropdown (changeable only for GA) used to fetch from
    // an effect gated on the shared navigationLists.wholesalersLoading flag, and
    // spin whenever that flag was true. Any other writer of the flag — the axios
    // request interceptor, the access-list bootstrap, a LOGOUT reset — could
    // therefore leave the spinner turning with no request behind it. Owning the
    // request here ties the spinner to this query's own fetch state instead.
    const {
        data: wholesalersData,
        isFetching: isWholesalersFetching,
        refetch: refetchWholesalers,
    } = useTanstackQuery({
        queryKey: ['wholesalerNavigationList'],
        queryFn: async () => {
            const res = await axios.get('/wholesalernavigationlist');
            return res.data;
        },
        enabled: isWAorAbove && shouldLoadWholesalers,
        refetchOnWindowFocus: false,
    });

    const isWholesalersListLoading = isWholesalersFetching;

    useEffect(() => {
        if (!wholesalersData) return;
        dispatch({
            type: 'CHANGE_WHOLESALERS',
            payload: wholesalersData,
        });
    }, [wholesalersData, dispatch]);

    // Kept in sync for the other consumers of the flag (e.g. CompanyAdd).
    useEffect(() => {
        dispatch({
            type: 'CHANGE_WHOLESALERS_LOADING',
            payload: isWholesalersFetching,
        });
    }, [isWholesalersFetching, dispatch]);

    // HOTFIX 2634: duplicated calls for wholesaler dropdown
    const {
        data: companiesAndPartnersData,
        isLoading: isCompaniesAndPartnersLoading,
    } = useTanstackQuery({
        queryKey: ['companyNavigationList', currentWholesaler],
        queryFn: async () => {
            const data = await getCompanyNavigationList(currentWholesaler);
            return relabel(data);
        },
        enabled: shouldLoadCompaniesAndPartners && !!currentWholesaler,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        dispatch({
            type: 'CHANGE_COMPANIES_AND_PARTNERS',
            payload: companiesAndPartnersData || [],
        });
    }, [companiesAndPartnersData, dispatch]);

    useEffect(() => {
        dispatch({
            type: 'CHANGE_COMPANIES_AND_PARTNERS_LOADING',
            payload: isCompaniesAndPartnersLoading,
        });
    }, [isCompaniesAndPartnersLoading, dispatch]);

    return (
        <>
            {changeWholesaler ? (
                <>
                    <div className={classes.search}>
                        <FormControl
                            id="wholesaler-dropdown"
                            data-walkthroughid={
                                walkthroughIds.ufSelectWholesalerDropdown
                            }
                            variant="floatingSelect"
                            sx={floatingSelectStyles}>
                            <Select
                                tabSelectsValue
                                openMenuOnFocus
                                placeholder=" "
                                onFocus={() => {
                                    // Arming the query fetches it; once armed,
                                    // refocusing refreshes the list.
                                    if (shouldLoadWholesalers) {
                                        refetchWholesalers();
                                    } else {
                                        setShouldLoadWholesalers(true);
                                    }
                                }}
                                value={wholesalers.find(
                                    (item) => item.id === currentWholesaler,
                                )}
                                onChange={(v) => {
                                    if (
                                        cannotNavigate({
                                            show,
                                            state,
                                            hash,
                                            message:
                                                'Are you sure you would like to switch to a different wholesaler? All unsaved changes will be lost.',
                                        })
                                    ) {
                                        return;
                                    }
                                    // HOTFIX 2634: duplicates wholesaler endpoint
                                    unstable_batchedUpdates(() => {
                                        dispatch({
                                            type: 'CHANGE_CURRENT_WHOLESALER',
                                            payload: v.id,
                                        });
                                        dispatch({
                                            type: 'CHANGE_CURRENT_COMPANY',
                                            payload:
                                                v.id === defaultWholesaler
                                                    ? defaultCompany
                                                    : null,
                                        });
                                        dispatch({
                                            type: 'CHANGE_CURRENT_PARTNER',
                                            payload:
                                                v.id === defaultWholesaler
                                                    ? defaultPartner
                                                    : null,
                                        });
                                    });
                                    // Ensure the companies/partners refetch effect fires even if
                                    // "Currently Managing" was never focused this session (#2633) —
                                    // otherwise companiesAndPartnersLoading stays stuck true.
                                    setShouldLoadCompaniesAndPartners(true);

                                    if (!currentPath.includes('/services')) {
                                        navigate(`/${currentParentPath}`);
                                    } else {
                                        navigate('/dashboard-services');
                                    }
                                }}
                                name="Wholesaler"
                                options={wholesalers.sort((left, right) =>
                                    left?.name.localeCompare(right?.name),
                                )}
                                isLoading={isWholesalersListLoading}
                                getOptionLabel={(v) => {
                                    if (typeof v === 'string') {
                                        if (v.length === 0) {
                                            return config.notAllocated.dropdown;
                                        }
                                        const result = wholesalers.find(
                                            (item) => item.id === v,
                                        );
                                        return result?.name ?? '';
                                    }
                                    return v?.name ?? '';
                                }}
                                getOptionValue={(option) => option.id}
                                chakraStyles={chakraStyles}
                                classNamePrefix="chakra-react-select"
                                colorScheme={colorScheme}
                                selectedOptionColor={colorScheme}
                            />
                            <FormLabel>Wholesaler</FormLabel>
                        </FormControl>
                    </div>
                    <ChakraIconButton
                        bg="transparent"
                        _hover={{
                            bg: 'whiteAlpha.300',
                        }}
                        color="#f5f5f5"
                        _active={{ bg: 'transparent' }}
                        className="!rounded-full"
                        onClick={() => {
                            setChangeWholesaler(false);
                        }}
                        icon={<CloseIcon fontSize="sm" />}
                    />
                </>
            ) : (
                <>
                    <ChakraIconButton
                        style={{
                            marginLeft: 0,
                            padding: 0,
                            width: 100,
                        }}
                        _hover={{ bg: 'transparent' }}
                        _active={{ bg: 'transparent' }}
                        bg={{ bg: 'transparent' }}
                        onClick={() => {
                            console.log('clicked');
                            handleNavigateLocation('/dashboard-services');
                        }}>
                        {imagePathLightMode && imagePathDarkMode && (
                            <div
                                style={{
                                    maxHeight: '54px',
                                    width: '100%',
                                    display: 'flex',
                                }}>
                                <Image
                                    alt="Home Logo"
                                    className="navbar-image"
                                    style={{
                                        display: 'block',
                                        maxHeight: '100%',
                                        maxWidth: '100%',
                                        margin: '0 auto',
                                    }}
                                    src={
                                        import.meta.env.VITE_APP_IMAGE_BASE +
                                        imagePathDarkMode
                                    }
                                />
                            </div>
                        )}
                    </ChakraIconButton>
                    {isGA && ( //only GA can change wholesaler
                        <ChakraIconButton
                            data-walkthroughid={
                                walkthroughIds.ufSelectWholesalerBtn
                            }
                            bg="transparent"
                            _hover={{
                                bg: 'whiteAlpha.300',
                            }}
                            color="#f5f5f5"
                            _active={{
                                bg: 'transparent',
                            }}
                            className="!rounded-full"
                            onClick={() => {
                                setChangeWholesaler(true);
                                setShouldLoadWholesalers(true);
                            }}
                            icon={<MdAccountTree fontSize="sm" />}
                        />
                    )}
                </>
            )}
            <div
                className={
                    changeWholesaler ? classes.search : classes.searchCompany
                }
                style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignSelf: 'center',
                }}>
                <FormControl
                    id="searchCompanyBar"
                    data-walkthroughid={
                        walkthroughIds.ufCurrentlyManagingDropdown
                    }
                    variant="floatingSelect"
                    sx={floatingSelectStyles}>
                    <Select
                        tabSelectsValue
                        openMenuOnFocus
                        placeholder=" "
                        value={currentManagingValue}
                        onFocus={() => {
                            // 2634: don't force loading:true here — the
                            // query's own isFetching state (synced above)
                            // already reflects whether a fetch is actually
                            // needed, so re-focusing an already-loaded list
                            // no longer triggers a redundant refetch.
                            setShouldLoadCompaniesAndPartners(true);
                        }}
                        filterOption={(candidate, input) => {
                            if (!input) return true;
                            const lower = input.toLowerCase();
                            const nameMatch = candidate.data.name
                                ?.toLowerCase()
                                .includes(lower);
                            const tenantMatch = candidate.data.microsoftTenantID
                                ? candidate.data.microsoftTenantID.toLowerCase() ===
                                  lower
                                : false;
                            return nameMatch || tenantMatch;
                        }}
                        onChange={(v) => {
                            if (
                                cannotNavigate({
                                    show,
                                    state,
                                    hash,
                                    message:
                                        'Are you sure you would like to switch to a different company? All unsaved changes will be lost.',
                                })
                            ) {
                                return;
                            }
                            // 2634: batch so currentCompany/currentPartner never
                            // update in separate renders — see the wholesaler
                            // onChange handler above for why this matters.
                            unstable_batchedUpdates(() => {
                                dispatch({
                                    type: 'CHANGE_CURRENT_COMPANY',
                                    payload: v.id,
                                });
                                dispatch({
                                    type: 'CHANGE_CURRENT_PARTNER',
                                    payload: v.parentID || v.id,
                                });
                            });

                            if (!currentPath.includes('/services')) {
                                navigate(`/${currentParentPath}`);
                            } else {
                                navigate('/dashboard-services');
                            }
                        }}
                        name="Currently Managing"
                        options={companyOptions}
                        isLoading={isCompaniesAndPartnersListLoading}
                        getOptionLabel={(v) => v?.name || currentCompanyLabel}
                        getOptionValue={(option) => option.id}
                        chakraStyles={chakraStyles}
                        classNamePrefix="chakra-react-select"
                        colorScheme={colorScheme}
                        selectedOptionColor={colorScheme}
                    />
                    <FormLabel>Currently Managing</FormLabel>
                </FormControl>
            </div>
        </>
    );
};

export default Searchbar;
